import { Autocomplete, Box, TextField } from '@mui/material'
import { FC, useCallback, useEffect, useState } from 'react'

import { Loader } from '~/shared/ui'
import { useMutation } from '~/shared/hooks/useMutation'
import {
  AvailablePhysicianSlot,
  availableSlotsByProvider,
  AvailableSlotsByProviderResponse,
  toWireReason,
  VisitReason,
} from '../AppointmentService'
import { endOfDay } from 'date-fns'
import PhysicianSlotDetails from './PhysicianSlotDetails'
import SelectDate from './SelectDate'
import CustomAppointment from './CustomAppointment'
import { physician, PhysicianListResponse } from '@fireflyhealth/api'
import { fireAndForget } from '~/shared/lib/fireAndForget'

const ROLES = [
  'Nurse Practitioner',
  'Physician',
  'Registered Nurse',
  'Behavioral Health Specialist',
  'Health Guide',
  'Other',
]

type BookAppointmentByProviderTabProps = {
  patientId: number
  visitReason: VisitReason | null
  now: Date
  date: Date
  setDate: (value: Date) => void
  slot: AvailablePhysicianSlot | null
  setSlot: (value: AvailablePhysicianSlot) => void
  setIsCustomAppointment: (value: boolean) => void
  setCustomAppointmentReasons: (value: string[]) => void
  setDurationMinutes: (minutes: number | null) => void
}

type Physician = PhysicianListResponse[number]

type ProviderOption = {
  label: string
  value: Physician | string
  isFixed?: boolean
}

const BookAppointmentByProviderTab: FC<BookAppointmentByProviderTabProps> = ({
  patientId,
  visitReason,
  now,
  date,
  setDate,
  slot,
  setSlot,
  setIsCustomAppointment,
  setCustomAppointmentReasons,
  setDurationMinutes,
}) => {
  const [physicianFilter, setPhysicianFilter] = useState<Record<string, Physician>>({})
  const [allOptions, setAllOptions] = useState<ProviderOption[]>([])
  const [showLoader, setShowLoader] = useState(false)
  const [selectedPhysician, setSelectedPhysician] = useState('')
  const [physicianSlots, setPhysicianSlots] = useState<AvailableSlotsByProviderResponse | null>(
    null,
  )
  const [allowedToBookCustomAppointment, setAllowedToBookCustomAppointment] = useState(false)

  // The factory is memoized so `useMutation` can keep `loadPhysicians` stable — it reads
  // nothing but the two setters below, which React keeps stable, and the effect that calls
  // it depends on its identity.
  const loadPhysicianOptions = useCallback(async () => {
    const { data: result } = await physician.list()
    const options: ProviderOption[] = []
    const physicianData: Record<string, Physician> = {}
    const physiciansByRole: Record<string, Physician[]> = {}
    result.forEach(physician => {
      const physicianName =
        physician.first_name + ' ' + physician.last_name + ', ' + physician.title
      physicianData[physicianName] = physician
      const role = ROLES.find(role => role === physician.provider.internal_role) ?? 'Other'
      physiciansByRole[role] = [...(physiciansByRole[role] ?? []), physician]
    })
    for (const role of ROLES) {
      const physicians = physiciansByRole[role]
      if (!physicians) continue

      options.push({ value: role, label: role + 's', isFixed: true })
      options.push(
        ...physicians.map(physician => ({
          label: physician.first_name + ' ' + physician.last_name + ', ' + physician.title,
          value: physician,
        })),
      )
    }
    setPhysicianFilter(physicianData)
    setAllOptions(options)
    return result
  }, [])

  const { loading: loadingPhysicians, handler: loadPhysicians } = useMutation(loadPhysicianOptions)

  useEffect(() => {
    void loadPhysicians()
  }, [loadPhysicians])

  const chosenPhysician = selectedPhysician ? physicianFilter[selectedPhysician] : undefined

  useEffect(() => {
    if (!chosenPhysician || visitReason == null) {
      return
    }
    // Changing the date or the provider starts another load while the last one may still
    // be open, so drop a response this effect has moved off: otherwise the slower one
    // wins and the grid shows one day's or one provider's slots under another's heading.
    let ignore = false
    const loadAvailableSlots = async () => {
      setShowLoader(true)
      const slots: AvailableSlotsByProviderResponse = await availableSlotsByProvider(
        chosenPhysician.id,
        patientId,
        visitReason,
        date,
        endOfDay(date),
      )
      if (ignore) {
        return
      }
      setAllowedToBookCustomAppointment(slots.allow_custom_appointment)
      setCustomAppointmentReasons([...slots.reasons_for_unavailability])
      setPhysicianSlots(slots)
      setDurationMinutes(slots.duration_minutes)

      setShowLoader(false)
    }
    fireAndForget('[Booking] load available slots', loadAvailableSlots)
    return () => {
      ignore = true
    }
  }, [
    date,
    chosenPhysician,
    patientId,
    visitReason,
    setCustomAppointmentReasons,
    setDurationMinutes,
  ])

  const onPhysicianChange = (selected: ProviderOption | null) => {
    setShowLoader(true)
    if (selected?.label) {
      setSelectedPhysician(selected.label)
    } else {
      setSelectedPhysician('')
      setPhysicianSlots(null)
      setDurationMinutes(null)
    }
    setShowLoader(false)
  }

  return (
    <>
      {(loadingPhysicians || showLoader) && <Loader />}
      <Box>
        <Box sx={{ paddingTop: 4, flexGrow: 1 }}>
          <Autocomplete
            size="small"
            options={allOptions}
            getOptionLabel={option => option.label}
            getOptionDisabled={option => option.isFixed === true}
            renderOption={({ key, ...props }, option) => (
              <li key={key} {...props}>
                {option.isFixed ? (
                  <Box
                    component="span"
                    sx={{ fontWeight: 500, fontSize: 15, color: 'text.primary', mt: '10px' }}
                  >
                    {option.label}
                  </Box>
                ) : (
                  <Box component="span" sx={{ fontWeight: 400, fontSize: 16, ml: 1 }}>
                    {option.label}
                  </Box>
                )}
              </li>
            )}
            renderInput={params => <TextField {...params} label="Provider" />}
            onChange={(_, selected) => {
              onPhysicianChange(selected)
            }}
            style={{ width: 350 }}
          />
        </Box>
      </Box>
      {physicianSlots && chosenPhysician && (
        <Box>
          <SelectDate
            date={date}
            setDate={setDate}
            now={now}
            datesWithSlotAvailability={physicianSlots.dates_with_slot_availability}
            bookingWindowInHours={physicianSlots.booking_window_in_hours}
          />
          <PhysicianSlotDetails
            physicianSlots={physicianSlots}
            physicianId={chosenPhysician.id}
            reason={visitReason}
            slot={slot}
            setSlot={setSlot}
            setDate={setDate}
            bookingWindowInHours={physicianSlots.booking_window_in_hours}
          />
        </Box>
      )}
      {chosenPhysician && allowedToBookCustomAppointment && (
        <CustomAppointment
          date={date}
          setSlot={setSlot}
          physicianId={chosenPhysician.id}
          reason={visitReason ? toWireReason(visitReason) : null}
          setIsCustomAppointment={setIsCustomAppointment}
        />
      )}
    </>
  )
}

export default BookAppointmentByProviderTab
